'use client';

import Label from '@/components/typography/label';
import P from '@/components/typography/p';
import { Case } from '@/payload-types';
import clsx from 'clsx';
import { Dot } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

interface CaseItemProps {
    index?: number;
    item: Case;
    isOverview?: boolean;
}

export function CaseItem({ item, isOverview = false, index }: CaseItemProps) {
    const banner = typeof item.banner === 'object' ? item.banner : undefined;
    const imageUrl = banner?.url ?? '/placeholder.svg';
    const imageAlt = banner?.alt ?? 'Banner afbeelding';

    const diensten =
        item.algemeneInformatie?.diensten?.map((d) =>
            typeof d.dienst === 'string' ? d.dienst : d.dienst.naam,
        ) || [];

    const klant = item.algemeneInformatie?.klant ?? '';
    const projectTitel = item.titel;
    const subtitle = `${klant.toUpperCase()} - ${projectTitel}`;

    return (
        <div
            className={clsx(
                'border-primary flex size-full flex-col items-center justify-start p-2.5',
                !isOverview
                    ? 'px-5 py-8 lg:px-[8vw] lg:py-[6.25rem] xl:px-[17.5vw]'
                    : 'px-6 py-8 not-last:border-b sm:border-0 lg:py-15',
            )}
        >
            <Link href={`/cases/${item.slug}`} className="w-full">
                <figure className="relative aspect-[9/5] h-auto w-full overflow-hidden rounded-lg lg:rounded-[20px]">
                    <Image
                        src={imageUrl}
                        alt={imageAlt}
                        className="bg-accent object-cover transition-transform duration-500 ease-in-out hover:scale-105 lg:min-h-[188px]"
                        fill
                    />
                </figure>
            </Link>
            <div className="mt-5 flex w-full flex-col justify-between gap-3 lg:flex-row lg:gap-0">
                <div className="flex items-center gap-3 lg:gap-8">
                    <div
                        className={clsx(
                            `relative flex w-fit items-center justify-center`,
                            isOverview ? 'hidden' : '',
                        )}
                    >
                        <svg
                            width="62"
                            height="38"
                            className="h-8 w-auto lg:h-10"
                            viewBox="0 0 62 38"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M31 0.95459C39.4853 0.95459 47.1433 3.02113 52.6631 6.33545C58.1925 9.65561 61.4998 14.1725 61.5 19.0679C61.5 23.9634 58.1927 28.481 52.6631 31.8013C47.1433 35.1156 39.4853 37.1821 31 37.1821C22.5147 37.1821 14.8567 35.1156 9.33691 31.8013C3.80731 28.481 0.5 23.9634 0.5 19.0679C0.50016 14.1725 3.80752 9.65561 9.33691 6.33545C14.8567 3.02113 22.5147 0.95459 31 0.95459Z"
                                stroke="#111110"
                            />
                        </svg>
                        {index !== undefined && (
                            <P className="absolute text-[14px]">
                                {index + 1}/3
                            </P>
                        )}
                    </div>
                    <Link href={`/cases/${item.slug}`} className="w-fit">
                        <P className="font-medium hover:underline">
                            {subtitle}
                        </P>
                    </Link>
                </div>
                <div className="flex min-w-[35%] flex-wrap items-center gap-1 lg:justify-end lg:gap-3">
                    {diensten.map((label) => (
                        <div
                            key={label}
                            className="border-primary inline-flex items-center gap-1 rounded-full border px-2 py-1 lg:gap-2 lg:px-3 lg:py-1.5"
                        >
                            <Dot
                                size={10}
                                className="scale-[5]"
                                strokeWidth={2}
                            />
                            <Label>{label}</Label>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

CaseItem.displayName = 'CaseItem';
export default CaseItem;
